// ─────────────────────────────────────────────
//  InvestmentCalculator.jsx
//  Calculadora de rendimientos de inversión
// ─────────────────────────────────────────────

import React, { useState, useCallback } from 'react';
import appStyles from '../layout/CalculatorPage.module.css';

import CapitalInput from './CapitalInput';
import RateSlider from './RateSlider';
import TermPicker from './TermPicker';
import OptionalFees from './OptionalFees';
import InvestmentSummary from './InvestmentSummary';
import GrowthTable from './GrowthTable';
import ToggleSwitch from '../tips/ToggleSwitch';

// termPos: 1–29 = días, 30–149 = meses (29 + months)
const DEFAULT_RATE     = 10;
const DEFAULT_TERM_POS = 41;   // 12 meses = 1 año
const DEFAULT_COMPOUND = true;

const TOGGLE_COLORS = {
	checkedBackground: '#007aff',
	checkedBorderColor: 'rgba(78, 78, 78, 0.99)',
	checkedThumbColor: '#ffffff',
	focusOutlineColor: 'rgba(0, 122, 255, 0.55)',
};

export default function InvestmentCalculator() {
	const [capital,   setCapital]   = useState('');
	const [ratePct,   setRatePct]   = useState(DEFAULT_RATE);
	const [termPos,   setTermPos]   = useState(DEFAULT_TERM_POS);
	const [isCompound, setIsCompound] = useState(DEFAULT_COMPOUND);
	const [comision,  setComision]  = useState('');
	const [isr,       setIsr]       = useState('');

	const parsedCapital  = parseFloat(capital)  || 0;
	const parsedComision = parseFloat(comision)  || 0;
	const parsedIsr      = parseFloat(isr)       || 0;
	const hasCapital     = parsedCapital > 0;

	// ── Math ──────────────────────────────────
	// Comisión anual resta de la tasa bruta
	const effectiveRate = Math.max(0, ratePct - parsedComision);
	const r = effectiveRate / 100;
	const isDays = termPos <= 29;

	let grossFinal  = 0;
	let grossGain   = 0;
	let isrAmount   = 0;
	let netGain     = 0;
	let netFinal    = 0;
	let returnPct   = 0;

	if (hasCapital) {
		if (isDays) {
			const days = termPos;
			grossFinal = isCompound
				? parsedCapital * Math.pow(1 + r / 365, days)
				: parsedCapital * (1 + r * (days / 365));
		} else {
			const months = termPos - 29;
			grossFinal = isCompound
				? parsedCapital * Math.pow(1 + r / 12, months)
				: parsedCapital * (1 + r * (months / 12));
		}

		grossGain = grossFinal - parsedCapital;
		isrAmount = grossGain * (parsedIsr / 100);
		netGain   = grossGain - isrAmount;
		netFinal  = parsedCapital + netGain;
		returnPct = (netGain / parsedCapital) * 100;
	}

	// ── Reset ─────────────────────────────────
	const handleReset = useCallback(() => {
		setCapital('');
		setRatePct(DEFAULT_RATE);
		setTermPos(DEFAULT_TERM_POS);
		setIsCompound(DEFAULT_COMPOUND);
		setComision('');
		setIsr('');
	}, []);

	return (
		<main className={appStyles.root} aria-label="Calculadora de inversión">

			{/* ── Top Bar ── */}
			<div className={appStyles.topBar}>
				<div className={appStyles.switchRow}>
					<ToggleSwitch
						label="Interés Compuesto"
						checked={isCompound}
						onChange={setIsCompound}
						ariaLabel="Calcular con interés compuesto"
						colors={TOGGLE_COLORS}
					/>
				</div>
			</div>

			{/* ── Capital Input ── */}
			<CapitalInput
				value={capital}
				onChange={setCapital}
				onReset={handleReset}
			/>

			{/* ── Rate Slider ── */}
			<RateSlider value={ratePct} onChange={setRatePct} />

			{/* ── Term Picker ── */}
			<TermPicker termPos={termPos} onChange={setTermPos} />

			{/* ── Optional Fees ── */}
			<OptionalFees
				comision={comision}
				onComisionChange={setComision}
				isr={isr}
				onIsrChange={setIsr}
			/>

			{/* ── Results Summary ── */}
			<InvestmentSummary
				capital={parsedCapital}
				grossGain={grossGain}
				isrAmount={isrAmount}
				netGain={netGain}
				netFinal={netFinal}
				returnPct={returnPct}
				ratePct={ratePct}
				effectiveRate={effectiveRate}
				comision={parsedComision}
				parsedIsr={parsedIsr}
				termPos={termPos}
				hasCapital={hasCapital}
				isCompound={isCompound}
			/>

			{/* ── Growth Table ── */}
			{hasCapital && (
				<GrowthTable
					capital={parsedCapital}
					effectiveRate={effectiveRate}
					termPos={termPos}
					parsedIsr={parsedIsr}
					isCompound={isCompound}
				/>
			)}

		</main>
	);
}
